import { connect } from 'react-redux';
import { useState, useEffect, useRef, Fragment  } from 'react';

import { setLoading, setAlert, siteboardAction } from '../actions/defaultActions';
import {
  Email, SiteboardApiValidator, UserName, GET_SCHEDULE_CONTACT_INVITE_LINK_DETAILS, STOP_USING_INVITE_SCHEDULE_CONTACT_LINK, INVITE_LINK_EXPIRE_TIME_DAYS, 
  REDEEM_SCHEDULE_CONTACT_INVITE_LINK, REQUEST_FRESH_SCHEDULE_CONTACT_INVITE_LINK,
} from '../actions/types';
import { getServerApiResponse } from '../Util';

import PoweredByBar from './PoweredByBar';
import ParallaxSection from './ParallaxSection';
import Tooltip from './Tooltip';
import PushButton from './PushButton';

import Logo from '../SiteBoard_Logo';
import BackIcon from '../icons/backIcon';
import InfoIcon from '../icons/infoIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';

const testMode = false;

const InviteSubscriptionContactSignupPage = ({_inviteLinkCode, _inviteLinkEmail, setAlert, setLoading, siteboardAction}) => {
  const inputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]';
  const disabledInputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] focus:bg-[#656565]';

  const [inputFirstName, setInputFirstName] = useState('');
  const [inputLastName, setInputLastName] = useState('');
  const [inputCompanyName, setInputCompanyName] = useState('');
  const [inputEmail, setInputEmail] = useState(_inviteLinkEmail);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviterName, setInviterName] = useState(true);
  const [inviterEmail, setInviterEmail] = useState(true);
  const [projectNames, setProjectNames] = useState([]);
  const [sentScheduleProjectNames, setSentScheduleProjectNames] = useState([]);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [alreadyRedeemed, setAlreadyRedeemed] = useState(false);
  const [newInviteSent, setNewInviteSent] = useState(false);
  const [successRedeemingInvite, setSuccessRedeemingInvite] = useState(false);

  const fetchInviteState = useRef('none');

  useEffect(() => {
    if(testMode) {
      setInputEmail('myemail@stuff.com');
      setIsExpired(false);
      setAlreadyRedeemed(false);
      setInviterName('Strongly Rickmanson');
      setInviterEmail('ricky.strong@construction.com');
      setProjectNames(['Ricks Warehouse', 'Two', 'Three', 'Four', 'Five']);
      setIsExistingUser(false);
      setInviteLoading(false);
      setSuccessRedeemingInvite(false);
      return;
    }

    if(fetchInviteState.current !== 'none') return;

    const onApiSuccess = (data) => {
      try {
        fetchInviteState.current = 'complete';
        const validOutput = SiteboardApiValidator[GET_SCHEDULE_CONTACT_INVITE_LINK_DETAILS].validateOutput(data); 
        setIsExpired(validOutput.isExpired);
        setAlreadyRedeemed(validOutput.alreadyRedeemed);
        setInviterName(validOutput.inviterName);
        setInviterEmail(validOutput.inviterEmail);
        setProjectNames(validOutput.projectNames);
        setIsExistingUser(validOutput.isExistingUser);
        setInviteLoading(false);
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      fetchInviteState.current = 'none';
      siteboardAction(STOP_USING_INVITE_SCHEDULE_CONTACT_LINK, {});
      window.history.pushState('', '', '?'); // clear the url
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting invite info');
    };
    getServerApiResponse({ 
      action: GET_SCHEDULE_CONTACT_INVITE_LINK_DETAILS,
      inviteEmail: _inviteLinkEmail,
      inviteCode: _inviteLinkCode,
    }, onApiSuccess, onApiError);

    fetchInviteState.current = 'pending';
  }, [_inviteLinkCode, _inviteLinkEmail, setAlert, siteboardAction]);

  const acceptInvite = () => {
    let apiArgs = null;
    try {
      apiArgs = SiteboardApiValidator[REDEEM_SCHEDULE_CONTACT_INVITE_LINK].validateInput({
        action: REDEEM_SCHEDULE_CONTACT_INVITE_LINK,
        firstName: inputFirstName,
        lastName: inputLastName,
        companyName: inputCompanyName,
        inviteEmail: _inviteLinkEmail,
        inviteCode: _inviteLinkCode,
      });
      if(apiArgs.firstName.length === 0) throw new Error('Please enter your first name');
      if(apiArgs.lastName.length === 0) throw new Error('Please enter your last name');
      if(apiArgs.companyName.length === 0) throw new Error('Please enter your company name');
    } catch(error) {
      setAlert(error.message);
      return;
    }

    const onApiSuccess = (data) => {
      setLoading('');
      try {
        const validOutput = SiteboardApiValidator[REDEEM_SCHEDULE_CONTACT_INVITE_LINK].validateOutput(data); 
        setSuccessRedeemingInvite(true);
        setSentScheduleProjectNames(validOutput.sentScheduleProjectNames);
        //siteboardAction(STOP_USING_INVITE_SCHEDULE_CONTACT_LINK, {});
        //window.history.pushState('', '', '?'); // clear the url
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'There was an error accepting your invite.');
    };
    setLoading('Subscribing Your Email');
    getServerApiResponse(apiArgs, onApiSuccess, onApiError);
  };

  const requestNewInvite = () => {
    const onApiSuccess = (data) => {
      setLoading('');
      setNewInviteSent(true);
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting invite info');
    };
    setLoading('Sending Invite Email');
    getServerApiResponse({ 
      action: REQUEST_FRESH_SCHEDULE_CONTACT_INVITE_LINK,
      inviteEmail: _inviteLinkEmail,
      inviteCode: _inviteLinkCode,
    }, onApiSuccess, onApiError);
  };

  const getProjectNamesJSX = (namesList) => {
    if(namesList.length === 0) return <p className='inline'>no projects</p>;
    return (
      <>
        {namesList.map((name, index) => (
          <Fragment key={index}>
            {(index === (namesList.length-1) && namesList.length > 1) && <p className='inline'> and </p> }
            {(index > 0 && index < (namesList.length-1) && namesList.length > 1) && <p className='inline'>, </p> }
            <p className='inline text-white'>{name}</p>
          </Fragment>
        ))}
      </>
    );
  };

  const getInviteJSX = () => {
    if(inviteLoading) return <></>;

    const footerInfo = (
      <div className='flex flex-col gap-2'>
        <div className='w-full h-[1px] bg-white my-4'></div>
        <div className='italic'>
          {process.env.REACT_APP_WEBSITE_NAME} lets you quickly create and update <span className='text-nowrap'>3-week</span> lookahead schedules without spreadsheets.
        </div>
        <div className='italic'>
          If you need assistance, contact us at 
          <a className='ml-1 text-[#f9a928]' href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</a>
        </div>
      </div>
    );

    if(isExpired) {
      return (
        <>
          {newInviteSent === false ? 
            <>
              <div>Your invite link is more than {INVITE_LINK_EXPIRE_TIME_DAYS} days old and has expired for security reasons. To proceed, we can send a new invitation to <b>{_inviteLinkEmail}</b>.</div>
              <div className='flex flex-col gap-4 bg-[#44444444] rounded-lg p-4'>
                <div className='font-medium'>Please click the button below to receive a fresh invite link via email.</div>
                <div className='flex flex-col gap-y-6'>
                  {/* submit email button */}
                  <PushButton 
                    textColor='black'
                    bgColor='#f9a928'
                    hoverBgColor='#f79800'
                    onClick={() => { requestNewInvite(); }}
                  >
                    Send New Invite Link
                  </PushButton>
                </div>
              </div>
            </>
            :
            <div>
              We sent a new invite link to <b>{_inviteLinkEmail}</b>. Please check your inbox and click the new link to accept your invitation. If you don’t see the email shortly, be sure to check your spam or junk folder.
            </div>
          }
          {footerInfo}
        </>
      );
    }

    if(alreadyRedeemed) {
      return (
        <div className='flex flex-col gap-4'>
          <div className='flex items-start gap-2 text-[1.2em] pt-4'>
            <CircleCheckIcon size='1.4em' color='#00bc7d' />
            <div>All set</div>
          </div>
          <div>You've already signed up to receive <span className='text-nowrap'>3-week</span> lookahead schedule updates.</div>
          <div>Schedules are sent to your email as PDF attachments, so keep a lookout for emails from {process.env.REACT_APP_WEBSITE_NAME}.</div>
          {/*<div>You can stop receiving these updates at any time by using the "unsubscribe" link at the bottom of any emails we send you.</div>*/}
          {footerInfo}
        </div>
      );
    }

    if(successRedeemingInvite) {
      return (
        <div className='flex flex-col gap-4'>
          <div className='flex items-start gap-2 text-[1.2em] pt-4'>
            <CircleCheckIcon size='1.4em' color='#00bc7d' />
            <div>All set</div>
          </div>
          <div>We sent you the most recent <span className='text-nowrap'>3-week</span> lookahead schedule{sentScheduleProjectNames.length > 1 ? 's' : ''} for <b>{getProjectNamesJSX(sentScheduleProjectNames)}</b> as {sentScheduleProjectNames.length > 1 ? 'PDF attachments' : 'a PDF attachment'}.</div>
          {/*<div>You can stop receiving these updates at any time by using the "unsubscribe" link at the bottom of any emails we send you.</div>*/}
          {footerInfo}
        </div>
      );
    }

    return (
      <>
        {/*<div className='flex flex-col gap-2 max-w-[90vw] overflow-x-auto'>
          <div className='font-bold text-[1.2em] text-[#f9a928]'>
            Confirm you want to receive <span className='text-nowrap'>3-week</span> lookahead schedules for {getProjectNamesJSX(projectNames)} from <div className='inline text-white'><span className='text-nowrap'>{inviterName}</span> (<span className='text-nowrap'>{inviterEmail}</span>)</div>.
          </div>
          <div className="text-white mt-2">
            <p className="font-bold mb-2 text-[1.2em] text-[#f9a928]">
              Once confirmed:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>We'll email you a PDF of the current schedule</li>
              <li>Future schedule updates will be sent to your email</li>
            </ul>
          </div>
        </div>*/}
        <div className='flex flex-col gap-4 bg-[#44444466] rounded-lg p-4'>
          <div className='font-medium'>Please confirm the information below to receive schedule updates for <b>{getProjectNamesJSX(projectNames)}</b></div>
          <div className='flex flex-col gap-y-6'>
            {/* First and Last name */}
            <div className='flex gap-x-2'>
              <div>
                <label className='block text-sm font-medium mb-1'>First Name</label>
                <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                  <input 
                    value={inputFirstName}
                    className={inputStyle}
                    type='text' 
                    onChange={(event) => { setInputFirstName(event.target.value); }}
                  />
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium mb-1'>Last Name</label>
                <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                  <input 
                    value={inputLastName}
                    className={inputStyle}
                    type='text' 
                    onChange={(event) => { setInputLastName(event.target.value); }}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium mb-1'>Company Name</label>
              <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                <input 
                  value={inputCompanyName}
                  className={inputStyle}
                  type='text' 
                  onChange={(event) => { setInputCompanyName(event.target.value); }}
                />
              </div>
            </div>
            {/* Email Input */}
            <div>
              <label className='block text-sm font-medium mb-1'>Email</label>
              <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                <input 
                  value={inputEmail}
                  className={disabledInputStyle}
                  type='text' 
                  disabled={true}
                  onChange={(event) => {}}
                />
              </div>
            </div>
            {/* submit email button */}
            <PushButton 
              textColor='black'
              bgColor='#f9a928'
              hoverBgColor='#f79800'
              onClick={() => { acceptInvite(); }}
            >
              Send Me the Schedule
            </PushButton>
          </div>
        </div>
      </>
    );
  };
  
  return (
    <div className={`relative flex flex-col justify-between min-h-screen bg-[#151515] text-white overflow-hidden`}>
      <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-sm px-2 py-6 text-[1.2em] w-full mx-auto'>
        <div className='grow flex flex-col justify-center items-center'>
          <div className='flex flex-col gap-6'>
            {/* SiteBoard logo */}
            <div>
              <a href='/' className={'flex items-center gap-x-2 font-bold text-[1.8em]'}>
                <div className={`${inviteLoading ? 'animate-[spin_1s_linear_infinite]' : ''}`}>
                  <Logo size={'1em'} />
                </div>
                <div className='flex flex-col'>
                {process.env.REACT_APP_WEBSITE_NAME} 
                </div>
              </a>
              {inviteLoading === true && 
                <div className='font-medium text-[#aaa] text-[1.2em]'>Retrieving Your Invitation</div>
              }
            </div>
            {getInviteJSX()}
          </div>
        </div>
      </div>
      <div className='z-[1]'>
        <PoweredByBar />
      </div>
      {/* construction site background */}
      <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none'>
        <ParallaxSection parallaxMultiplier={0.5}>
          {/*<img width='906' height='1000' className='h-[120vh] object-cover object-top' src='/images/constructionSite2.svg' alt='line drawing of a construction site' />*/}
          <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
          {/* gradient at bottom to fade image into gray background */}
          <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
        </ParallaxSection>
      </div>
      {/* top right floating logo */}
      <div className='fixed top-[4em] left-[100%] translate-x-[-90%] opacity-[0.10] pointer-events-none'>
        <div className='sm:hidden'><Logo size={250} /></div>
        <div className='hidden sm:block 2xl:hidden'><Logo size={300} /></div>
        <div className='hidden 2xl:block'><Logo size={400} /></div>
      </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _inviteLinkCode: state.localEphemeral.inviteSubscriptionContactLinkCode,
  _inviteLinkEmail: state.localEphemeral.inviteSubscriptionContactLinkEmail,
}};
export default connect(mapStateToProps, {setAlert, setLoading, siteboardAction})(InviteSubscriptionContactSignupPage);
